import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/login.css';
import { authService } from '../services/authService';
import LoginNetworkBackground from '../components/LoginNetworkBackground';

// The cover's plain field, mirrored from --lg-ground in login.css.
const GROUND = '#174a5b';

// The pre-enter hold: the animated ground plays alone, then the card fades in.
const SPLASH_MS = 3000;

const prefersReducedMotion = () => {
  try {
    return typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M12 7.5v5" /><path d="M12 16.2h.01" />
  </svg>
);

const IconEye = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.6 6.1A9.6 9.6 0 0 1 12 6c6 0 9.5 6 9.5 6a17 17 0 0 1-2.7 3.4" />
    <path d="M6.3 7.8A16.7 16.7 0 0 0 2.5 12S6 18 12 18a9.5 9.5 0 0 0 4-.85" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3 3l18 18" />
  </svg>
);

const LoginPage = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Sent back here by AuthWatcher because the session lapsed mid-work.
  const arrivedFromExpiry = location.state?.reason === 'expired';
  // 'hold'  the cover plays alone behind the progress bar
  // 'form'  the sign-in card is up
  //
  // Someone who has asked for reduced motion gets no hold at all — a 3s wait
  // with nothing on screen is exactly the thing that setting exists to avoid.
  // Neither does someone who was just bounced out by an expired session: they
  // were working a moment ago, and the card should be right there.
  const [phase, setPhase] = useState(() => (prefersReducedMotion() || arrivedFromExpiry ? 'form' : 'hold'));
  const revealed = phase === 'form';

  useEffect(() => {
    if (arrivedFromExpiry) setError('Your session has expired. Please sign in again.');
  }, [arrivedFromExpiry]);

  // Load saved credentials on component mount
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const rememberMeStatus = localStorage.getItem('rememberMe') === 'true';

    if (rememberMeStatus && savedUsername && savedPassword) {
      setFormData({ username: savedUsername, password: savedPassword });
      setRememberMe(true);
    }
  }, []);

  // Pre-enter hold. Staff sign in here every day, so the wait is skippable:
  // a click, a key or a touch brings the card in straight away. Waiting it out
  // gives the full three seconds.
  useEffect(() => {
    if (phase !== 'hold') return undefined;

    const reveal = () => setPhase('form');
    const timer = window.setTimeout(reveal, SPLASH_MS);

    window.addEventListener('pointerdown', reveal, { once: true });
    window.addEventListener('keydown', reveal, { once: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', reveal);
      window.removeEventListener('keydown', reveal);
    };
  }, [phase]);

  // index.css deliberately paints no page background, so an overscroll bounce
  // would flash white behind the ground. Paint it only while the login screen
  // is mounted, and hand it back on the way out so the dashboard is unaffected.
  useEffect(() => {
    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = GROUND;
    return () => { document.body.style.backgroundColor = previous; };
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    try {
      await authService.login(formData);

      // Handle remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberedUsername', formData.username);
        localStorage.setItem('rememberedPassword', formData.password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberMe');
      }

      // Client-side navigation, NOT window.location.href. That was a full
      // document reload: the browser tore the page down, App.js came back up
      // through its "Loading…" screen, and the version check could reload a
      // second time — the visible flash after signing in. Nothing needs the
      // reload: /dashboard is guarded by ProtectedRoute, which reads the token
      // straight from storage, and the token is already written by now.
      // `replace` keeps the sign-in form out of the back-button history.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      await delay(2000);
      console.log('Login error:', err); // Debug log

      // Handle session conflict error specifically
      if (err.message && err.message.includes('already logged in')) {
        setError(err.message);
      } else if (err.response?.data?.message) {
        // Backend error message
        setError(err.response.data.message);
      } else if (err.message) {
        // Network or other error
        setError(err.message);
      } else {
        setError('Login failed. Please check your username and password.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const version = 'BioMed Research Centre Software version 1.1.6';

  return (
    <div className="login-page">
      <LoginNetworkBackground />

      {/* The white lockup holds the corner rather than sitting in a bar, so the
          cover keeps its single field — same placement as the app shell. It is
          outside the reveal, so the mark is there through the hold and does not
          blink when the card arrives. */}
      <img
        className="login-logo"
        src={`${process.env.PUBLIC_URL}/biomed-white.png`}
        width="140"
        height="28"
        alt="Biomed Technology"
      />

      {/* Boot-style progress bar. Its fill is driven by the same SPLASH_MS the
          reveal timer uses, so the bar reaching the end and the card arriving
          are the same moment — it reports the real wait rather than miming one.
          Skipping early cuts both short together. */}
      <div
        className={`login-progress${phase === 'hold' ? '' : ' is-done'}`}
        style={{ '--splash': `${SPLASH_MS}ms` }}
        role="progressbar"
        aria-label="Loading"
        aria-hidden={phase === 'hold' ? undefined : 'true'}
      >
        <i />
      </div>

      <div
        className={`login-shell${revealed ? ' is-revealed' : ''}`}
        aria-hidden={revealed ? undefined : 'true'}
      >
        <main className="login-main">
          <p className="login-eyebrow login-anim" style={{ '--i': 1 }}>Staff access</p>
          <h2 className="login-title login-anim" style={{ '--i': 2 }}>Sign in</h2>
          <p className="login-sub login-anim" style={{ '--i': 2 }}>
            Use your employee account to continue.
          </p>

          {error && (
            <div className="login-error" role="alert" id="login-error">
              <IconAlert />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate={false}>
            <div className="login-field login-anim" style={{ '--i': 3 }}>
              <label className="login-label" htmlFor="login-username">Username</label>
              <span className="login-input-wrap">
                <input
                  id="login-username"
                  type="text"
                  name="username"
                  placeholder="employee.name"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck="false"
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </span>
            </div>

            <div className="login-field login-anim" style={{ '--i': 4 }}>
              <label className="login-label" htmlFor="login-password">Password</label>
              <span className="login-input-wrap has-toggle">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                />
                <button
                  type="button"
                  className="login-reveal"
                  onClick={() => setShowPassword(v => !v)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <IconEyeOff /> : <IconEye />}
                </button>
              </span>
            </div>

            <div className="login-row login-anim" style={{ '--i': 5 }}>
              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                />
                <span>Remember me</span>
              </label>
            </div>

            <button
              type="submit"
              className="login-submit login-anim"
              style={{ '--i': 6 }}
              disabled={isLoading}
            >
              {isLoading && <span className="login-spinner" aria-hidden="true" />}
              {isLoading ? 'Signing in…' : 'Log in'}
            </button>
          </form>

          <div className="login-foot">
            Trouble signing in? Contact your laboratory administrator.
            <div className="login-foot-version" style={{ marginTop: 8 }}>{version}</div>
          </div>
        </main>
      </div>

      {isLoading && (
        <div className="login-overlay" role="status" aria-live="polite">
          <div className="login-overlay-spinner" />
          <div className="login-overlay-text">Signing you in…</div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
